import React, { useState } from 'react';
import api from '../api/api';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { Payment as PaymentIcon, Phone as PhoneIcon } from '@mui/icons-material';

interface MpesaPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  subscriptionId?: number;
  invoiceId?: number;
  amount: number;
  title: string;
  description: string;
}

interface PaymentStep {
  label: string;
  description: string;
}

const steps: PaymentStep[] = [
  {
    label: 'Enter Phone Number',
    description: 'Enter the M-Pesa registered phone number',
  },
  {
    label: 'Confirm Payment',
    description: 'Review payment details and confirm',
  },
  {
    label: 'Complete Payment',
    description: 'Check your phone for M-Pesa prompt',
  },
];

export const MpesaPaymentDialog: React.FC<MpesaPaymentDialogProps> = ({
  open,
  onClose,
  onSuccess,
  subscriptionId,
  invoiceId,
  amount,
  title,
  description,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [transactionId, setTransactionId] = useState<string | null>(null);

  const handleNext = () => {
    if (activeStep === 0) {
      // Validate phone number
      if (!phoneNumber || phoneNumber.length < 10) {
        setError('Please enter a valid phone number');
        return;
      }
      setError(null);
      setActiveStep(1);
    } else if (activeStep === 1) {
      // Initiate payment
      initiatePayment();
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    setError(null);
  };

  const initiatePayment = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/mpesa/stk-push', {
        subscription_id: subscriptionId,
        invoice_id: invoiceId,
        phone_number: phoneNumber,
        amount: amount,
        account_reference: subscriptionId ? `SUB_${subscriptionId}` : `INV_${invoiceId}`,
        transaction_description: description,
      });

      const data = response.data;

      if (data.success) {
        setTransactionId(data.transaction_id);
        setActiveStep(2);
        setSuccess(true);
        
        // Start polling for payment status
        pollPaymentStatus(data.transaction_id);
      } else {
        setError(data.detail || 'Failed to initiate payment');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (txnId: string) => {
    const maxAttempts = 30; // 5 minutes with 10-second intervals
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await api.get(`/mpesa/transactions/${txnId}`);
        const data = response.data;
          
        if (data.status === 'success') {
          onSuccess();
          onClose();
          return;
        } else if (data.status === 'failed') {
          setError('Payment failed. Please try again.');
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 10000); // Poll every 10 seconds
        } else {
          setError('Payment timeout. Please check your phone and try again.');
        }
      } catch (err) {
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 10000);
        }
      }
    };

    setTimeout(poll, 10000); // Start polling after 10 seconds
  };

  const handleClose = () => {
    if (!loading) {
      setActiveStep(0);
      setPhoneNumber('');
      setError(null);
      setSuccess(false);
      setTransactionId(null);
      onClose();
    }
  };

  const formatPhoneNumber = (value: string) => {
    // Remove all non-digit characters
    const cleaned = value.replace(/\D/g, '');
    
    // Format as Kenyan phone number
    if (cleaned.length <= 3) {
      return cleaned;
    } else if (cleaned.length <= 6) {
      return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
    } else {
      return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 10)}`;
    }
  };

  const handlePhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(event.target.value);
    setPhoneNumber(formatted);
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter the M-Pesa registered phone number that will receive the payment prompt.
            </Typography>
            <TextField
              fullWidth
              label="Phone Number"
              value={phoneNumber}
              onChange={handlePhoneNumberChange}
              placeholder="07X XXX XXX"
              helperText="Enter phone number in format: 07XXXXXXXX"
              InputProps={{
                startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Please review the payment details before proceeding.
            </Typography>
            <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                Payment Summary
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Amount:</strong> KES {amount.toLocaleString()}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Phone Number:</strong> {phoneNumber}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Description:</strong> {description}
              </Typography>
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Payment Initiated
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Please check your phone for the M-Pesa prompt and enter your PIN to complete the payment.
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Transaction ID: {transactionId}
            </Alert>
            <Typography variant="body2" color="text.secondary">
              This dialog will close automatically once payment is confirmed.
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PaymentIcon sx={{ mr: 1, color: 'primary.main' }} />
          M-Pesa Payment
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {renderStepContent(activeStep)}
      </DialogContent>

      <DialogActions>
        {activeStep < 2 && (
          <>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleBack}
              disabled={activeStep === 0 || loading}
            >
              Back
            </Button>
            <Button
              onClick={handleNext}
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <PaymentIcon />}
            >
              {activeStep === 1 ? 'Pay Now' : 'Next'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}; 